// src/pages/Dashboard.tsx
import React, { useState, ChangeEvent } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { SelectChangeEvent } from "@mui/material/Select";

// Función para formatear fecha a "dd-mm-aaaa"
const formatDate = (dateStr: string) => {
  const [year, month, day] = dateStr.split("-");
  return `${day}-${month}-${year}`;
};

interface Turno {
  id: number;
  fecha: string; // formato YYYY-MM-DD
  hora: string; // formato HH:mm
  cancha: string;
  titular: string;
  estado: string;
  adelanto: boolean;
}

const mockTurnos: Turno[] = [
  {
    id: 1,
    fecha: "2025-02-13",
    hora: "10:00",
    cancha: "Cancha 1",
    titular: "Juan Pérez",
    estado: "Activo",
    adelanto: true,
  },
  {
    id: 2,
    fecha: "2025-02-13",
    hora: "12:00",
    cancha: "Cancha 2",
    titular: "María López",
    estado: "Activo",
    adelanto: false,
  },
  {
    id: 3,
    fecha: "2025-02-13",
    hora: "14:00",
    cancha: "Cancha 1",
    titular: "Carlos Ruiz",
    estado: "En proceso de reventa",
    adelanto: true,
  },
  {
    id: 4,
    fecha: "2025-02-13",
    hora: "16:00",
    cancha: "Cancha 3",
    titular: "Ana Gómez",
    estado: "Revendido",
    adelanto: false,
  },
  {
    id: 5,
    fecha: "2025-02-13",
    hora: "11:00",
    cancha: "Cancha 2",
    titular: "Lucía Méndez",
    estado: "Activo",
    adelanto: true,
  },
  {
    id: 6,
    fecha: "2025-02-13",
    hora: "09:00",
    cancha: "Cancha 1",
    titular: "Roberto Díaz",
    estado: "Activo",
    adelanto: false,
  },
  {
    id: 7,
    fecha: "2025-02-13",
    hora: "15:00",
    cancha: "Cancha 3",
    titular: "Marcos Fuentes",
    estado: "Activo",
    adelanto: true,
  },
  {
    id: 8,
    fecha: "2025-02-18",
    hora: "17:00",
    cancha: "Cancha 2",
    titular: "Sofía Carrizo",
    estado: "Activo",
    adelanto: false,
  },
  {
    id: 9,
    fecha: "2025-02-19",
    hora: "18:00",
    cancha: "Cancha 1",
    titular: "Diego Torres",
    estado: "Activo",
    adelanto: true,
  },
  {
    id: 10,
    fecha: "2025-03-20",
    hora: "08:00",
    cancha: "Cancha 3",
    titular: "Claudia Vargas",
    estado: "Activo",
    adelanto: false,
  },
  {
    id: 11,
    fecha: "2025-03-21",
    hora: "10:30",
    cancha: "Cancha 1",
    titular: "Esteban Ruiz",
    estado: "Activo",
    adelanto: true,
  },
];

const rowsPerPage = 10;

const months = [
  { value: "1", label: "Enero" },
  { value: "2", label: "Febrero" },
  { value: "3", label: "Marzo" },
  { value: "4", label: "Abril" },
  { value: "5", label: "Mayo" },
  { value: "6", label: "Junio" },
  { value: "7", label: "Julio" },
  { value: "8", label: "Agosto" },
  { value: "9", label: "Septiembre" },
  { value: "10", label: "Octubre" },
  { value: "11", label: "Noviembre" },
  { value: "12", label: "Diciembre" },
];

const canchas = ["Cancha 1", "Cancha 2", "Cancha 3"];

// Función para renderizar el Chip del estado
const renderEstadoChip = (estado: string) => {
  switch (estado) {
    case "Activo":
      return <Chip label={estado} color="success" />;
    case "En proceso de reventa":
      return <Chip label={estado} color="warning" />;
    case "Revendido":
      return <Chip label={estado} color="info" />;
    default:
      return <Chip label={estado} />;
  }
};

const Dashboard: React.FC = () => {
  // Valor por defecto del mes: mes actual
  const currentMonth = (new Date().getMonth() + 1).toString();
  // Valor por defecto del día: día actual en formato "DD"
  const currentDay = new Date().getDate().toString().padStart(2, "0");

  const [filterMonth, setFilterMonth] = useState<string>(currentMonth);
  const [filterDay, setFilterDay] = useState<string>(currentDay);
  const [filterHour, setFilterHour] = useState<string>("");
  const [filterCancha, setFilterCancha] = useState<string>("");

  const [page, setPage] = useState<number>(1);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedTurno, setSelectedTurno] = useState<Turno | null>(null);

  const handleMonthChange = (e: SelectChangeEvent<string>) => {
    setFilterMonth(e.target.value);
    setPage(1);
  };

  const handleDayChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFilterDay(e.target.value);
    setPage(1);
  };

  const handleHourChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFilterHour(e.target.value);
    setPage(1);
  };

  const handleCanchaChange = (e: SelectChangeEvent<string>) => {
    setFilterCancha(e.target.value);
    setPage(1);
  };

  // Filtrar turnos según mes, día, hora y cancha.
  const filteredTurnos = mockTurnos.filter((turno) => {
    const turnoMonth = parseInt(turno.fecha.substr(5, 2), 10).toString();
    const turnoDay = turno.fecha.substr(8, 2);
    const matchMonth = turnoMonth === filterMonth;
    const matchDay = filterDay ? turnoDay === filterDay.padStart(2, "0") : true;
    const matchHour = filterHour ? turno.hora === filterHour : true;
    const matchCancha = filterCancha ? turno.cancha === filterCancha : true;
    return matchMonth && matchDay && matchHour && matchCancha;
  });

  const paginatedTurnos = filteredTurnos.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleOpenModal = (turno: Turno) => {
    setSelectedTurno(turno);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedTurno(null);
    setOpenModal(false);
  };

  const handleGenerarLink = () => {
    if (selectedTurno) {
      alert(
        `Link generado para turno ID: ${selectedTurno.id}\nhttps://app.demo/confirmar-turno/${selectedTurno.id}`
      );
    }
    handleCloseModal();
  };

  const currentMonthLabel =
    months.find((m) => m.value === filterMonth)?.label || "";

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Turnos diarios
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" color="text.secondary">
          Mes {currentMonthLabel}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Total de turnos: {filteredTurnos.length}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, mb: 4, mt: 4 }}>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel id="month-label">Mes</InputLabel>
          <Select
            labelId="month-label"
            label="Mes"
            value={filterMonth}
            onChange={handleMonthChange}
          >
            {months.map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Día"
          type="number"
          InputLabelProps={{ shrink: true }}
          value={filterDay}
          onChange={handleDayChange}
          sx={{ width: 80 }}
          placeholder="DD"
        />
        <TextField
          label="Hora"
          type="time"
          InputLabelProps={{ shrink: true }}
          value={filterHour}
          onChange={handleHourChange}
          sx={{ minWidth: 120 }}
          inputProps={{ step: 1800 }}
        />
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel id="cancha-label">Cancha</InputLabel>
          <Select
            labelId="cancha-label"
            label="Cancha"
            value={filterCancha}
            onChange={handleCanchaChange}
          >
            <MenuItem value="">Todas</MenuItem>
            {canchas.map((cancha) => (
              <MenuItem key={cancha} value={cancha}>
                {cancha}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Fecha</TableCell>
              <TableCell>Hora</TableCell>
              <TableCell>Cancha</TableCell>
              <TableCell>Titular</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Cobro</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTurnos.map((turno) => (
              <TableRow key={turno.id}>
                <TableCell>{formatDate(turno.fecha)}</TableCell>
                <TableCell>{turno.hora}</TableCell>
                <TableCell>{turno.cancha}</TableCell>
                <TableCell>
                  {turno.estado === "En proceso de reventa"
                    ? ""
                    : turno.titular}
                </TableCell>
                <TableCell>{renderEstadoChip(turno.estado)}</TableCell>
                <TableCell>
                  {turno.adelanto ? (
                    <Chip label="Cobrado" color="success" />
                  ) : (
                    <Chip label="No cobrado" color="warning" />
                  )}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenModal(turno)}>
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedTurnos.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No se encontraron turnos.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
        <Pagination
          count={Math.ceil(filteredTurnos.length / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Acciones para turno #{selectedTurno?.id}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            Seleccione una acción para el turno.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleGenerarLink} color="primary">
            Generar Link
          </Button>
          <Button onClick={handleCloseModal} color="secondary">
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
